import { useState, useEffect, useMemo } from 'react';
import Card from 'components/card/Card';
import { Flex, Text, useColorModeValue } from '@chakra-ui/react';
import {
  PaginationState,
  useReactTable,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { SubscriptionPlan } from 'services/@types';
import SubscriptionPlanService from 'services/subscriptionPlan.api';
import useColumns from './hooks/useColumns';
import {
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Button,
  Select,
} from '@chakra-ui/react';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_INDEX = 1;

export default function SubscriptionPlansOverview() {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const pages = useMemo(() => {
    const arr = [];
    for (let i = 1; i <= totalPages; i++) arr.push(i);
    return arr;
  }, [totalPages]);

  const columns = useColumns();

  const table = useReactTable({
    data: plans,
    columns,
    state: { columnFilters },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  useEffect(() => {
    const fetch = async () => {
      try {
        const data = await SubscriptionPlanService.getSubscriptionPlans({
          limit: pageSize,
          page: pageIndex,
        });
        // API may return array directly or paginated object
        if (Array.isArray(data)) {
          setPlans(data);
          setTotalPages(1);
        } else if (data?.results) {
          setPlans(data.results);
          setTotalPages(data.totalPages);
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetch();
  }, [pageSize, pageIndex]);

  return (
    <Flex direction="column" pt={{ sm: '125px', lg: '75px' }}>
      <Card px="0px">
        <Flex direction="column" w="100%" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
          <Flex px="25px" mb="8px" justify="space-between" align="center">
            <Text color={textColor} fontSize="22px" fontWeight="700">
              Subscription Plans
            </Text>
          </Flex>
          <Table variant="simple" color="gray.500" mb="24px">
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th key={header.id} borderColor={borderColor}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table.getRowModel().rows.map((row) => (
                <Tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id} borderColor={borderColor}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
          <Flex px="25px" pb="20px" justify="space-between" align="center">
            <Flex gap="8px" align="center">
              <Select
                size="sm"
                w="80px"
                borderRadius="8px"
                value={pageSize}
                onChange={(e) =>
                  setPagination((prev) => ({
                    ...prev,
                    pageSize: Number(e.target.value),
                  }))
                }
              >
                {[10, 25, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </Select>
              <Text fontSize="sm" color="gray.500">
                per page
              </Text>
            </Flex>
            <Flex gap="4px">
              {pages.map((page) => (
                <Button
                  key={page}
                  size="sm"
                  variant={page === pageIndex ? 'brand' : 'ghost'}
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, pageIndex: page }))
                  }
                >
                  {page}
                </Button>
              ))}
            </Flex>
          </Flex>
        </Flex>
      </Card>
    </Flex>
  );
}
